import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
} from '@mui/material';
import {
  CollectionAiPolicyDocument,
  CollectionRole,
  parseCollectionAiPolicyDocument,
} from '@qkindex/contracts';
import {
  getCollectionAiPolicy,
  updateCollectionAiPolicy,
} from '../../../firebase/functions';

export function CollectionAiSettings({
  collectionId,
  role,
}: {
  collectionId: string;
  role: CollectionRole;
}) {
  const [policy, setPolicy] = useState<CollectionAiPolicyDocument>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const version = useRef(0);
  // Keep the operation identity on transport failure so retry cannot duplicate a write.
  const intent = useRef<
    | {
        collectionId: string;
        operationId: string;
        expectedRevision: number;
        editorAiEnabled: boolean;
      }
    | undefined
  >(undefined);
  useEffect(() => {
    const current = ++version.current;
    setPolicy(undefined);
    setError('');
    setSaving(false);
    intent.current = undefined;
    if (role !== 'owner') return;
    void getCollectionAiPolicy({ collectionId })
      .then((result) => {
        if (current === version.current)
          setPolicy(parseCollectionAiPolicyDocument(result.data));
      })
      .catch(() => {
        if (current === version.current)
          setError(
            'AI settings could not be loaded. Reopen settings to retry.',
          );
      });
    return () => {
      version.current += 1;
    };
  }, [collectionId, role]);
  if (role !== 'owner') return null;
  const loading = (!policy || policy.collectionId !== collectionId) && !error;
  const save = async (enabled: boolean) => {
    if (!policy || policy.collectionId !== collectionId || saving) return;
    const current = version.current;
    const request =
      intent.current?.editorAiEnabled === enabled
        ? intent.current
        : {
            collectionId,
            operationId: crypto.randomUUID(),
            expectedRevision: policy.revision,
            editorAiEnabled: enabled,
          };
    intent.current = request;
    setSaving(true);
    setError('');
    try {
      const result = await updateCollectionAiPolicy(request);
      if (current !== version.current) return;
      setPolicy({
        ...policy,
        revision: result.data.revision,
        editorAiEnabled: result.data.editorAiEnabled,
      });
      intent.current = undefined;
    } catch {
      if (current === version.current)
        setError(
          'AI settings could not be saved. Try again, or reopen settings to refresh.',
        );
    } finally {
      if (current === version.current) setSaving(false);
    }
  };
  return (
    <Stack spacing={1}>
      <FormControlLabel
        label={
          <Stack
            component="span"
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <span>AI for editors and administrators</span>
            {(loading || saving) && (
              <CircularProgress
                size={16}
                sx={{ flexShrink: 0 }}
                aria-label={
                  loading ? 'Loading AI settings' : 'Saving AI settings'
                }
              />
            )}
          </Stack>
        }
        labelPlacement="start"
        sx={{
          m: 0,
          justifyContent: 'space-between',
          gap: 2,
          '& .MuiFormControlLabel-label': {
            typography: 'body2',
            fontWeight: 600,
          },
        }}
        control={
          <Switch
            checked={
              policy?.collectionId === collectionId && policy.editorAiEnabled
            }
            disabled={!policy || policy.collectionId !== collectionId || saving}
            onChange={(event) => {
              void save(event.target.checked);
            }}
          />
        }
      />
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}
